import { useMemo, useState } from "react";
import { useConfig } from "../contexts/ConfigContext";
import { Dashboard } from "../types";
import { Box, Typography } from "@mui/material";
import ToolsNavigation from "../components/tools/ToolsNavigation";
import ToolsContainer from "../components/tools/ToolsContainer";

const Tools = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const config = useConfig();

  const dashboards: Dashboard[] = useMemo(() => {
    try {
      // Prefer new toolsUrls naming
      if (config.toolsUrls && Array.isArray(config.toolsUrls)) {
        console.log("[DashboardsContainer] Using toolsUrls configuration");
        return config.toolsUrls.filter((tool) => tool && tool.url); // Filter out invalid entries
      }

      // Fallback to dashboardUrls for backward compatibility
      if (config.dashboardUrls && Array.isArray(config.dashboardUrls)) {
        console.log("[DashboardsContainer] Using dashboardUrls configuration (backward compatibility)");
        return config.dashboardUrls.filter((dashboard) => dashboard && dashboard.url);
      }

      // Fallback to single dashboardUrl for backward compatibility
      if (config.dashboardUrl && typeof config.dashboardUrl === "string") {
        console.log("[DashboardsContainer] Using single dashboardUrl configuration (backward compatibility)");
        return [{ name: "Dashboard", url: config.dashboardUrl }];
      }

      console.log("[DashboardsContainer] No dashboard/tools configuration found");
      return [];
    } catch (error) {
      console.error("[DashboardsContainer] Error processing dashboard configuration:", error);
      return [];
    }
  }, [config]);

  // State for the currently selected dashboard
  const [currentDashboard, setCurrentDashboard] = useState<Dashboard | null>(dashboards.length > 0 ? dashboards[0] : null);

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  // Reset loading state when dashboard changes
  const handleDashboardChange = (dashboard: Dashboard) => {
    setIsLoading(true);
    setHasError(false);
    setCurrentDashboard(dashboard);
  };

  if (dashboards.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          alignItems: "center",
          justifyContent: "center",
          p: 4,
          textAlign: "center"
        }}
      >
        <Typography variant='h5' color='text.secondary' gutterBottom>
          No External Tools Available
        </Typography>
        <Typography variant='body1' color='text.secondary' sx={{ maxWidth: 600 }}>
          No external tools have been configured for this organisation yet. We can develop custom tools for your specific use cases -
          contact your administrator to discuss your requirements.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
      <ToolsNavigation dashboards={dashboards} handleDashboardChange={handleDashboardChange} currentDashboard={currentDashboard} />

      <ToolsContainer
        currentDashboard={currentDashboard}
        handleIframeError={handleIframeError}
        handleIframeLoad={handleIframeLoad}
        hasError={hasError}
        isLoading={isLoading}
      />
    </Box>
  );
};

export default Tools;
